/**
 * Frontend half of the entity backlink registry.
 *
 * Maps each provider key registered backend-side (see
 * `shared/services/entity_backlink_registry.py` and
 * `docs/platform/entity-relationship-surfaces-design.md` §4.2) to the panel the
 * contributing module renders for it. A detail page resolves its tabs from
 * `GET /api/v1/entity-links/{subject}/{id}/related-counts` and renders each one
 * through this map; a key with no entry here is skipped (and logged) rather than
 * crashing the host page. `tools/check_backlink_provider_parity.py` holds the two
 * halves in parity.
 *
 * **The lazy thunk is mandatory, and the reason is measured.** The repo's usual
 * answer for cross-module UI — promoting a component into `src/shared/` as a
 * re-export shim — is bundler-transparent: to Rollup it is the same static edge,
 * and there are 112 such shim edges today. Vite's `manualChunks` splits vendor
 * packages only, so app-code chunking is 100% emergent from `import()`
 * boundaries. A static map would pull every contributing module's panel — and
 * everything those panels import — into ContactDetailPage's chunk (29 KB →
 * ~150-400 KB). A `lazy(() => import())` map costs ~1-3 KB plus one round trip
 * on first panel render.
 *
 * **The entry is split for the same reason.** Tab labels, counts and permission
 * gating all render BEFORE the panel mounts, so only `Component` may be lazy —
 * `labelI18nKey` and `moduleId` have to be reachable synchronously or the tab
 * strip could not be drawn without downloading every panel it is deciding about.
 * (`moduleId` is also what drops a disabled module's tab before any request.)
 */

import { lazy, type ComponentType, type LazyExoticComponent } from 'react';
import {
  Banknote,
  BookOpen,
  ClipboardList,
  ClipboardCheck,
  DollarSign,
  FileSignature,
  FileSpreadsheet,
  FileInput,
  Gavel,
  Landmark,
  ListChecks,
  Megaphone,
  PackageCheck,
  Receipt,
  ShoppingCart,
  Ticket,
  Undo2,
  UserSearch,
  Wallet,
  type LucideIcon,
} from 'lucide-react';

/** Props every contributed backlink panel receives from the shared host. */
export interface BacklinkPanelProps {
  /** Canonical `module.entity` key of the subject being looked at. */
  subjectType: string;
  /** The subject entity's id. */
  subjectId: string;
}

export interface BacklinkPanelEntry {
  /** The provider key — matches the backend registry key exactly. */
  id: string;
  /**
   * The tab's URL-visible key, and NOT the same thing as `id`.
   *
   * `id` is the provider's registry identity (`pipeline_deals`); this is the
   * slug the host page's tab strip uses, which comes from the subject entity's
   * `tabs` list in `modules_entities.config.json` via `ENTITY_TABS` — so
   * `?tab=deals` keeps resolving and every bookmark and cross-page link
   * survives. They are deliberately separate: the provider key must be unique
   * across every module, while the tab key only has to be unique on its page.
   *
   * Getting this wrong renders an EMPTY tab: `useTabWithUrl` rejects a key that
   * is not in `ENTITY_TABS`, so the trigger draws and the panel never does.
   * `tools/check_backlink_provider_parity.py` checks it against the catalogue
   * for every subject the provider declares.
   */
  tabKey: string;
  /**
   * Namespaced i18n key for the tab label, resolving from the contributing
   * module's namespace. Mirrors the backend descriptor's `title_i18n_key`; the
   * parity gate checks both spell the same key.
   */
  labelI18nKey: string;
  /**
   * The contributing module's id. The tab is dropped before render and before
   * any request when this module is not accessible to the viewer.
   */
  moduleId: string;
  /**
   * The referencing entity's icon (STYLE_GUIDE § Entity icons). Synchronous for
   * the same reason the label is: the tab strip draws before the panel loads.
   */
  icon: LucideIcon;
  /** The panel itself — lazy, always. */
  Component: LazyExoticComponent<ComponentType<BacklinkPanelProps>>;
}

export const BACKLINK_PANELS: Record<string, BacklinkPanelEntry> = {
  accounting_bills: {
    id: 'accounting_bills',
    tabKey: 'bills',
    labelI18nKey: 'accounting:backlinks.bills.title',
    moduleId: 'accounting',
    icon: Banknote,
    Component: lazy(() =>
      import('../../../modules/accounting/components/backlinks/BillsBacklinkPanel').then((m) => ({
        default: m.BillsBacklinkPanel,
      }))
    ),
  },
  accounting_invoices: {
    id: 'accounting_invoices',
    tabKey: 'invoices',
    labelI18nKey: 'accounting:backlinks.invoices.title',
    moduleId: 'accounting',
    icon: Receipt,
    Component: lazy(() =>
      import('../../../modules/accounting/components/backlinks/InvoicesBacklinkPanel').then(
        (m) => ({ default: m.InvoicesBacklinkPanel })
      )
    ),
  },
  accounting_payments: {
    id: 'accounting_payments',
    tabKey: 'payments',
    labelI18nKey: 'accounting:backlinks.payments.title',
    moduleId: 'accounting',
    icon: Wallet,
    Component: lazy(() =>
      import('../../../modules/accounting/components/backlinks/PaymentsBacklinkPanel').then(
        (m) => ({ default: m.PaymentsBacklinkPanel })
      )
    ),
  },
  accounting_claims: {
    id: 'accounting_claims',
    tabKey: 'claims',
    labelI18nKey: 'accounting:backlinks.claims.title',
    moduleId: 'accounting',
    icon: Gavel,
    Component: lazy(() =>
      import('../../../modules/accounting/components/backlinks/ClaimsBacklinkPanel').then((m) => ({
        default: m.ClaimsBacklinkPanel,
      }))
    ),
  },
  accounting_vendor_statements: {
    id: 'accounting_vendor_statements',
    tabKey: 'vendor-statements',
    labelI18nKey: 'accounting:backlinks.vendorStatements.title',
    moduleId: 'accounting',
    icon: FileSpreadsheet,
    Component: lazy(() =>
      import(
        '../../../modules/accounting/components/backlinks/VendorStatementsBacklinkPanel'
      ).then((m) => ({ default: m.VendorStatementsBacklinkPanel }))
    ),
  },
  accounting_bank_accounts: {
    id: 'accounting_bank_accounts',
    tabKey: 'bank-accounts',
    labelI18nKey: 'accounting:backlinks.bankAccounts.title',
    moduleId: 'accounting',
    icon: Landmark,
    Component: lazy(() =>
      import('../../../modules/accounting/components/backlinks/BankAccountsBacklinkPanel').then(
        (m) => ({ default: m.BankAccountsBacklinkPanel })
      )
    ),
  },
  contracts_contracts: {
    id: 'contracts_contracts',
    tabKey: 'contracts',
    labelI18nKey: 'contracts:backlinks.contracts.title',
    moduleId: 'contracts',
    icon: FileSignature,
    Component: lazy(() =>
      import('../../../modules/contracts/components/backlinks/ContractsBacklinkPanel').then(
        (m) => ({ default: m.ContractsBacklinkPanel })
      )
    ),
  },
  pipeline_deals: {
    id: 'pipeline_deals',
    tabKey: 'deals',
    labelI18nKey: 'pipeline:backlinks.deals.title',
    moduleId: 'pipeline',
    icon: DollarSign,
    Component: lazy(() =>
      import('../../../modules/pipeline/components/backlinks/DealsBacklinkPanel').then((m) => ({
        default: m.DealsBacklinkPanel,
      }))
    ),
  },
  procurement_purchase_orders: {
    id: 'procurement_purchase_orders',
    tabKey: 'purchase-orders',
    labelI18nKey: 'procurement:backlinks.purchaseOrders.title',
    moduleId: 'procurement',
    icon: ShoppingCart,
    Component: lazy(() =>
      import(
        '../../../modules/procurement/components/backlinks/PurchaseOrdersBacklinkPanel'
      ).then((m) => ({ default: m.PurchaseOrdersBacklinkPanel }))
    ),
  },
  procurement_goods_receipts: {
    id: 'procurement_goods_receipts',
    tabKey: 'goods-receipts',
    labelI18nKey: 'procurement:backlinks.goodsReceipts.title',
    moduleId: 'procurement',
    icon: PackageCheck,
    Component: lazy(() =>
      import(
        '../../../modules/procurement/components/backlinks/GoodsReceiptsBacklinkPanel'
      ).then((m) => ({ default: m.GoodsReceiptsBacklinkPanel }))
    ),
  },
  procurement_rfqs: {
    id: 'procurement_rfqs',
    tabKey: 'rfqs',
    labelI18nKey: 'procurement:backlinks.rfqs.title',
    moduleId: 'procurement',
    icon: FileInput,
    Component: lazy(() =>
      import('../../../modules/procurement/components/backlinks/RfqsBacklinkPanel').then(
        (m) => ({ default: m.RfqsBacklinkPanel })
      )
    ),
  },
  procurement_supplier_pis: {
    id: 'procurement_supplier_pis',
    tabKey: 'supplier-quotes',
    labelI18nKey: 'procurement:backlinks.supplierPis.title',
    moduleId: 'procurement',
    icon: ClipboardCheck,
    Component: lazy(() =>
      import(
        '../../../modules/procurement/components/backlinks/SupplierPisBacklinkPanel'
      ).then((m) => ({ default: m.SupplierPisBacklinkPanel }))
    ),
  },
  procurement_supplier_claims: {
    id: 'procurement_supplier_claims',
    tabKey: 'supplier-claims',
    labelI18nKey: 'procurement:backlinks.supplierClaims.title',
    moduleId: 'procurement',
    icon: Gavel,
    Component: lazy(() =>
      import(
        '../../../modules/procurement/components/backlinks/SupplierClaimsBacklinkPanel'
      ).then((m) => ({ default: m.SupplierClaimsBacklinkPanel }))
    ),
  },
  procurement_vendor_returns: {
    id: 'procurement_vendor_returns',
    tabKey: 'vendor-returns',
    labelI18nKey: 'procurement:backlinks.vendorReturns.title',
    moduleId: 'procurement',
    icon: Undo2,
    Component: lazy(() =>
      import(
        '../../../modules/procurement/components/backlinks/VendorReturnsBacklinkPanel'
      ).then((m) => ({ default: m.VendorReturnsBacklinkPanel }))
    ),
  },
  recruiting_candidates: {
    id: 'recruiting_candidates',
    tabKey: 'referred-candidates',
    labelI18nKey: 'recruiting:backlinks.referredCandidates.title',
    moduleId: 'recruiting',
    icon: UserSearch,
    Component: lazy(() =>
      import(
        '../../../modules/recruiting/components/backlinks/ReferredCandidatesBacklinkPanel'
      ).then((m) => ({ default: m.ReferredCandidatesBacklinkPanel }))
    ),
  },
  sales_orders: {
    id: 'sales_orders',
    tabKey: 'orders',
    labelI18nKey: 'sales:backlinks.orders.title',
    moduleId: 'sales',
    icon: ShoppingCart,
    Component: lazy(() =>
      import('../../../modules/sales/components/backlinks/OrdersBacklinkPanel').then((m) => ({
        default: m.OrdersBacklinkPanel,
      }))
    ),
  },
  software_factory_development: {
    id: 'software_factory_development',
    tabKey: 'development',
    labelI18nKey: 'softwareFactory:backlinks.development.title',
    moduleId: 'software_factory',
    icon: ClipboardList,
    Component: lazy(() =>
      import(
        '../../../modules/software_factory/components/backlinks/DevelopmentBacklinkPanel'
      ).then((m) => ({
        default: m.DevelopmentBacklinkPanel,
      }))
    ),
  },
  support_kb_articles: {
    id: 'support_kb_articles',
    tabKey: 'knowledge-base',
    labelI18nKey: 'support:backlinks.kbArticles.title',
    moduleId: 'support',
    icon: BookOpen,
    Component: lazy(() =>
      import('../../../modules/support/components/backlinks/KBArticlesBacklinkPanel').then(
        (m) => ({ default: m.KBArticlesBacklinkPanel })
      )
    ),
  },
  support_tickets: {
    id: 'support_tickets',
    tabKey: 'tickets',
    labelI18nKey: 'support:backlinks.tickets.title',
    moduleId: 'support',
    icon: Ticket,
    Component: lazy(() =>
      import('../../../modules/support/components/backlinks/TicketsBacklinkPanel').then((m) => ({
        default: m.TicketsBacklinkPanel,
      }))
    ),
  },
  software_factory_linked_tasks: {
    id: 'software_factory_linked_tasks',
    tabKey: 'tasks',
    labelI18nKey: 'softwareFactory:backlinks.linkedTasks.title',
    moduleId: 'software_factory',
    icon: ListChecks,
    Component: lazy(() =>
      import(
        '../../../modules/software_factory/components/backlinks/LinkedTasksBacklinkPanel'
      ).then((m) => ({
        default: m.LinkedTasksBacklinkPanel,
      }))
    ),
  },
  software_factory_changelog_entries: {
    id: 'software_factory_changelog_entries',
    tabKey: 'changelog',
    labelI18nKey: 'softwareFactory:backlinks.changelog.title',
    moduleId: 'software_factory',
    icon: Megaphone,
    Component: lazy(() =>
      import(
        '../../../modules/software_factory/components/backlinks/ChangelogBacklinkPanel'
      ).then((m) => ({
        default: m.ChangelogBacklinkPanel,
      }))
    ),
  },
};

/**
 * Read one string off a row's provider-owned `meta` bag.
 *
 * The schema declares `additionalProperties: {"type": "string"}`, but the
 * generator widens an open map to `unknown` values, so every panel would
 * otherwise repeat the same cast. One place to narrow it, and one place to
 * change if the bag ever stops being string-only — which is also why panels read
 * through this rather than indexing `meta` directly.
 */
export function metaString(
  meta: Record<string, unknown> | null | undefined,
  key: string
): string | undefined {
  const value = meta?.[key];
  return typeof value === 'string' ? value : undefined;
}
